import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './RecentTransactions.module.scss';
import { useLazyGetCustomerTransactionsQuery } from '../../../../service/transaction-service';
import {
  Transaction,
  getTransactionTitleAndCategory,
  getTransactionStatusForDisplay,
  getRefundStatusForDisplay,
  TransactionItem,
} from '../../../../model/transaction';
import { formatCurrency } from '../../../../utils';
import { ElectricityBillsQuickServiceIcon, HistIcon } from '../../../../assets/icons';
import { ApplicationRoutes } from '../../../../routes';
import RefundTransactionModal from '../../../../common/components/refund-transaction-modal/RefundTransactionModal';

const STATUS_CLASSES: Record<string, string> = {
  successful: 'successful',
  pending: 'pending',
  aedcPending: 'pending',
  failed: 'failed',
  refunded: 'refunded',
  refundInProgress: 'refundInProgress',
  refundQueued: 'refundQueued',
  expired: 'expired',
  cancelled: 'cancelled',
  unknown: 'unknown',
};

const STATUS_TEXT_KEYS: Record<string, string> = {
  successful: 'common.labels.completed',
  pending: 'common.labels.inProgress',
  aedcPending: 'common.labels.pending',
  failed: 'common.labels.failed',
  refunded: 'history.web.refunded',
  refundInProgress: 'history.refund.inProgressTitle',
  refundQueued: 'history.refund.refundQueued',
  expired: 'common.labels.expired',
  cancelled: 'common.labels.cancelled',
  confirmed: 'common.labels.confirmed',
  declined: 'common.labels.declined',
  unknown: 'common.labels.unknown',
};

interface TxRowProps {
  transaction: TransactionItem;
  onClick: (id: string, category: number) => void;
}

/** Exported so mockups can render real-looking rows from dummy data (see UtilityPhonePreview). */
export const TxRow: React.FC<TxRowProps> = ({ transaction, onClick }) => {
  const { t } = useTranslation();
  const meta = getTransactionTitleAndCategory(transaction, t);
  const status = getRefundStatusForDisplay(transaction.refundStatus) ?? getTransactionStatusForDisplay(transaction.status);
  const amount = formatCurrency(transaction.amount.amount, { currency: transaction.amount.currency });
  const date = new Date(transaction.createdAt).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const statusText: string = STATUS_TEXT_KEYS[status] ? t(STATUS_TEXT_KEYS[status] as any) : status;

  return (
    <button
      className={styles.txRow}
      onClick={() => onClick(transaction.id, meta.category)}
      type="button"
    >
      <div className={styles.txIcon}>{meta.icon ?? <HistIcon />}</div>
      <div className={styles.txLeft}>
        <span className={styles.txTitle}>{meta.title}</span>
        <span className={styles.txDate}>{date}</span>
      </div>
      <div className={styles.txRight}>
        <span className={styles.txAmount}>{amount}</span>
        <span className={`${styles.txStatus} ${styles[STATUS_CLASSES[status]] ?? ''}`}>
          {statusText}
        </span>
      </div>
    </button>
  );
};

const EmptyState = () => {
  const { t } = useTranslation();
  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>
        <ElectricityBillsQuickServiceIcon />
      </div>
      <p className={styles.emptyText}>{t('history.yourTransactionsWillAppearHere')}</p>
    </div>
  );
};

export const RecentTransactions = React.memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [getTransactions, { data, isFetching }] = useLazyGetCustomerTransactionsQuery();
  const [refundReference, setRefundReference] = useState<string | null>(null);

  useEffect(() => {
    getTransactions({ page: 1, pageSize: 5 });
  }, []);

  const transactions: TransactionItem[] = data?.items ?? [];

  const handleClick = (id: string, category: number) => {
    const tx = transactions.find(t => t.id === id);
    if (!tx) return;
    // The refund screen looks the refund up by the original transaction's reference
    if (tx.refundStatus != null) {
      setRefundReference(tx.reference);
      return;
    }
    navigate(`/history/${tx.reference}?category=${category}`, { state: { category } });
  };

  return (
    <div className={styles.card}>

      <div className={styles.body}>
        {isFetching && transactions.length === 0 && (
          <div className={styles.loadingList}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={styles.skeletonRow} />
            ))}
          </div>
        )}

        {!isFetching && transactions.length === 0 && <EmptyState />}

        {transactions.map(tx => (
          <TxRow key={tx.id} transaction={tx} onClick={handleClick} />
        ))}
      </div>

      {transactions.length > 0 && (
        <button className={styles.seeAllBtn} onClick={() => navigate(`/${ApplicationRoutes.CUSTOMER_TRANSACTION_HISTORY}`)}>
          {t('home.web.seeAllTransactions')}
        </button>
      )}
      {refundReference && <RefundTransactionModal reference={refundReference} onClose={() => setRefundReference(null)} />}
    </div>
  );
});
